import {HStack, Icon, IconButton, Image, VStack, Wrap} from "@chakra-ui/react";
import React from "react";
import {ArrowBigDown, ArrowBigUp, Trash2} from "lucide-react";
import objectUtil from "@/utils/objectUtil";
import SelectView from "@/components/form/view/SelectView";
import CheckboxView from "@/components/form/view/CheckboxView";
import RadioView from "@/components/form/view/RadioView";
import InputView from "@/components/form/view/InputView";
import NumberInputView from "@/components/form/view/NumberInputView";
import {useLanguage} from "@/contexts/languageContext";

export const fieldCorresponding = {
    select: {
        name: 'fieldTypes.uniqueSelection',
        component: SelectView,
        defaultValue: null
    },
    checkbox: {
        name: 'fieldTypes.multipleChoice',
        component: CheckboxView,
        defaultValue: []
    },
    radio: {
        name: 'fieldTypes.uniqueChoice',
        component: RadioView,
        defaultValue: null
    },
    text: {
        name: 'fieldTypes.textualField',
        component: InputView,
        defaultValue: null
    },
    number: {
        name: 'fieldTypes.numeralField',
        component: NumberInputView,
        defaultValue: null
    },
}

export const FormBuilder = ({fields, errors, canEdit, onEdit, onChange, ...props}) => {

    const { language, t } = useLanguage();

    return <VStack {...props}>
        {(fields?.[language] && fields?.[language]?.length > 0)
            && fields[language].map((field, i) =>
                <HStack key={`field-${i}`} w="100%" alignItems="stretch" position="relative" pt={4} mb={2}>
                    {
                        canEdit &&
                        [<IconButton
                            key={`delete-button-${i}`}
                            borderRadius="50%"
                            size="sm"
                            position="absolute"
                            top=".5rem"
                            right=".5rem"
                            onClick={() => {
                                onEdit(Object.fromEntries(Object.entries(fields).map(([lang, value]) => {
                                    return [lang, objectUtil.deleteObjectByIndex(value, i)];
                                })))
                            }}
                            aria-label={t('sentences.deleteField')}
                            zIndex={fields[language].length * 2 - i}
                        >
                            <Icon as={Trash2}/>
                        </IconButton>,
                            <VStack
                                key={`field-${i}`}
                            >
                                {
                                    (i !== 0) &&
                                    <IconButton
                                        borderRadius="50%"
                                        aria-label={t('actions.moveDown')}
                                        onClick={() => {
                                            onEdit(Object.fromEntries(Object.entries(fields).map(([lang, value]) =>
                                                [lang, objectUtil.swapObjects(value, i, i - 1)]
                                            )))
                                        }}
                                        {...(i === (fields[language].length - 1) ? {'marginBottom': 'auto'} : {})}
                                    >
                                        <Icon as={ArrowBigUp}/>
                                    </IconButton>
                                }
                                {
                                    (i !== (fields[language].length - 1)) &&
                                    <IconButton
                                        borderRadius="50%"
                                        aria-label={t('actions.moveUp')}
                                        onClick={() => {
                                            onEdit(Object.fromEntries(Object.entries(fields).map(([lang, value]) =>
                                                [lang, objectUtil.swapObjects(value, i, i + 1)]
                                            )))
                                        }}
                                        {...(i === 0 ? {'marginTop': 'auto'} : {})}
                                    >
                                        <Icon as={ArrowBigDown}/>
                                    </IconButton>
                                }
                            </VStack>]
                    }
                    <VStack flex={1} align="stretch" gap={2}>
                        {
                            (field?.images?.length ?? 0) > 0 &&
                            <Wrap>
                                {field.images.map((url, imageIndex) => (
                                    <Image
                                        key={`field-${i}-image-${imageIndex}`}
                                        src={url}
                                        alt=""
                                        maxH="160px"
                                        borderRadius="8px"
                                        objectFit="cover"
                                    />
                                ))}
                            </Wrap>
                        }
                        {
                            fieldCorresponding[field.type].component &&
                            React.createElement(fieldCorresponding[field.type].component, {
                            ...(canEdit ? {} : {
                                errors: errors[i],
                                onChange: (value) => {
                                    onChange(i, value)
                                }
                            }),
                                zIndex: fields[language].length * 2 - i - 1,
                                position: 'relative', flex: 1,
                                ...objectUtil.filterByKeys(field, ['type', 'images'], false)
                        })
                        }
                    </VStack>
                </HStack>)
        }
    </VStack>
}

export default FormBuilder
